import type { ProcessingJob } from "features/dt-plus-setup/model/types";
import type { ExecutionStatus } from "features/shared/model/types";

import { customApiCall } from "utils/axios";

import { isRecord, parseNumericId, parseStringId } from "../lib/parseApiId";
import { TRANSIENT_JOB_CPU_COUNT, TRANSIENT_JOB_SUBTYPE, TRANSIENT_RESPONSE_ALGORITHM } from "../model/constants";
import { transientJobUrls } from "./urls";

const EXECUTION_STATUSES: ExecutionStatus[] = [
  "completed",
  "done",
  "error",
  "failed",
  "pending",
  "processing",
  "queued",
  "running",
  "waiting",
];

const parseExecutionStatus = (value: unknown): ExecutionStatus =>
  EXECUTION_STATUSES.find((status) => status === value) ?? "queued";

export const parseTrConfigId = (value: unknown): number => {
  const configId = parseNumericId(value, ["id", "config_id", "configId"]);
  if (configId == null) throw new Error("Missing configuration id");
  return configId;
};

export const parseSubmittedProcessingJob = (value: unknown): ProcessingJob => {
  const jobId = parseStringId(value, ["job_id", "jobId", "runId", "run_id", "id"]);
  if (!jobId) throw new Error("Missing job id");
  const row = isRecord(value) ? value : {};
  const nested = isRecord(row.data) ? row.data : row;

  const configId = parseNumericId(value, ["config_id", "configId"]);
  return {
    algorithm: TRANSIENT_RESPONSE_ALGORITHM,
    config_id: configId ?? undefined,
    executed_at: typeof nested.executed_at === "string" ? nested.executed_at : new Date().toISOString(),
    job_id: jobId,
    status: parseExecutionStatus(nested.status),
  };
};

export const submitTransientJob = async ({
  configId,
  projectId,
}: {
  configId: number;
  projectId: string;
}): Promise<ProcessingJob> => {
  const response = await customApiCall.post({
    data: {
      configId,
      cpuCount: TRANSIENT_JOB_CPU_COUNT,
      projectId: Number(projectId),
      subType: TRANSIENT_JOB_SUBTYPE,
    },
    url: transientJobUrls.submit(),
  });
  const job = parseSubmittedProcessingJob(response);
  return { ...job, config_id: job.config_id ?? configId };
};
